import { ReactNode, useEffect, useState } from "react";
import { HighlightColours } from "../../../models/HighlightColors";
import { DlfMatcher } from "../../../models/matchers/DlfMatcher";
import { KeyValueMatcher } from "../../../models/matchers/KeyValueMatcher";
import { RegexFieldMatcher } from "../../../models/matchers/RegexFieldMatcher";
import { getDlfMatcherTypesDisplayValue } from "../../../types/rules/DlfMatcherTypes";
import { Button } from "../../buttons/Button";
import { useAsync } from "../../hooks/useAsync";
import {
  TTKeyValueMatcherKeyLabel,
  TTKeyValueMatcherValueLabel,
  TTKeyValueMatcherValueSuggestions,
} from "../../tooltips/ConfigurationTooltips";
import {
  FieldMatcherEditor,
  FieldMatcherEditorChildProps,
} from "../FieldMatcherEditor";
import { RegexFieldMatcherEditorWithSuggestions } from "../regex/RegexFieldMatcherEditorWithSuggestions";
import "../../../styles/dlfEditing/kvDlfEditor.css";

type Props = {
  valueButton?: ReactNode;
  matcher: KeyValueMatcher;
  /**
   * Method that will attempt to intelligently populate the Key Matcher.
   * If undefined, no Populate functionality will be seen
   */
  populateKey?: (dlf: KeyValueMatcher) => Promise<KeyValueMatcher | undefined>;
} & FieldMatcherEditorChildProps<KeyValueMatcher>;

// TODO: rename KeyValueFieldMatcherEditor
const KeyValueDlfEditor = (props: Props) => {
  // 'Populate' button appears when KeyValueMatcher's keyMatcher is not configured
  const showPopulateKeyField = !props.matcher.keyMatcher.isConfigured();

  const [result, trigger, running] = useAsync<KeyValueMatcher | undefined>(
    async () => {
      if (props.populateKey) {
        return (
          (await props
            .populateKey(props.matcher)
            .catch((e) => console.error("Could not populate KVM", e))) ??
          undefined
        );
      }
      return undefined;
    },
    undefined,
  );

  // Update the Key Dlf Matcher when Result changes
  useEffect(() => {
    // BaseField was null
    if (!result) return;

    props.matcher.keyMatcher = result.keyMatcher;
    props.update(props.matcher);
  }, [result]);

  const keySectionCoordinate = props.matcher.keyMatcher.section;
  const valueSectionCoordinate = props.matcher.valueMatcher.section;

  if (keySectionCoordinate.getSectionType() !== "PAGE") {
    throw new Error(
      `KeyValueDlfEditor does not support Key Sections with discriminator ${keySectionCoordinate.getSectionType()}`,
    );
  }

  if (valueSectionCoordinate.getSectionType() !== "PAGE") {
    throw new Error(
      `KeyValueDlfEditor does not support Value Sections with discriminator ${valueSectionCoordinate.getSectionType()}`,
    );
  }

  let keyValueDlfPopulateStatus: "HIDE" | "SHOW" | "RUNNING";
  if (showPopulateKeyField && props.populateKey) {
    keyValueDlfPopulateStatus = running ? "RUNNING" : "SHOW";
  } else {
    keyValueDlfPopulateStatus = "HIDE";
  }

  return (
    // use
    <div className={"fieldMatcherEditor kvDlfEditor"}>
      <FieldMatcherEditor
        key={props.matcher.keyMatcher.matcher.getMatcherId() + "KEY"}
        label={`${getDlfMatcherTypesDisplayValue(props.matcher.keyMatcher.matcher.getDiscriminator())} Key`}
        changedMatcherType={props.changedMatcherType}
        labelColor={"black"}
        labelBgColor={HighlightColours.getKvKeyColor()}
        labelTooltip={TTKeyValueMatcherKeyLabel}
        matcher={props.matcher.keyMatcher.matcher}
        docFieldConfig={props.docFieldConfig}
        update={(matcher: DlfMatcher) => {
          props.matcher.keyMatcher.matcher = matcher;
          props.matcher.keyMatcher.threshold = matcher.threshold;
          // regex has changed, and is valid. now extract !
          props.update(props.matcher);
        }}
        // 'Populate' button
        button={
          keyValueDlfPopulateStatus && keyValueDlfPopulateStatus !== "HIDE" ? (
            <Button onClick={() => trigger()}>
              {keyValueDlfPopulateStatus === "SHOW" && "Populate"}
              {keyValueDlfPopulateStatus === "RUNNING" && "Populating"}
            </Button>
          ) : undefined
        }
      />

      <RegexFieldMatcherEditorWithSuggestions
        key={props.matcher.valueMatcher.matcher.getMatcherId() + "VALUE"}
        label={"Value"}
        labelColor={"black"}
        labelBgColor={HighlightColours.getHighlightColor(props.index ?? 0)}
        labelTooltip={TTKeyValueMatcherValueLabel}
        matcher={props.matcher.valueMatcher.matcher as RegexFieldMatcher}
        docFieldConfig={props.docFieldConfig}
        button={props.valueButton}
        suggestions={props.docFieldConfig.validator.validationRules.map(
          (x) => ({ regex: x.regex, label: x.label }),
        )}
        suggestionsTooltip={TTKeyValueMatcherValueSuggestions}
        suggestionsTooltipOverflow
        update={(matcher: DlfMatcher) => {
          props.matcher.valueMatcher.matcher = matcher;
          // regex has changed, and is valid. now extract !
          props.update(props.matcher);
        }}
      />
    </div>
  );
};

export { KeyValueDlfEditor, Props as KeyValueDlfEditorProps };
